import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTimeIT } from "@/lib/formatters";

const LAB_PREFIX = "[LAB]";
export const LAB_SAFETY_MESSAGE =
  "LABORATORIO - solo clienti fittizi. Nessun messaggio reale inviato. Nessun cliente reale deve essere usato.";

export const isLabCustomer = (customer) => {
  const normalizedName = String(customer?.name || "").trim().toUpperCase();
  return normalizedName.startsWith(LAB_PREFIX);
};

const formatValue = (value, fallback = "Non disponibile") => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return fallback;
  }
  return String(value);
};

const formatDateTime = (value, fallback = "Non disponibile") => {
  if (!value) {
    return fallback;
  }
  return formatDateTimeIT(value);
};

const buildCustomerAppUrl = (path) => {
  if (typeof window === "undefined") {
    return path;
  }
  return new URL(path, window.location.origin).toString();
};

const isExpired = (value) => {
  if (!value) {
    return false;
  }
  const expiresAtMs = Date.parse(value);
  return Number.isFinite(expiresAtMs) && expiresAtMs < Date.now();
};

export const getReminderStatusPresentation = (reminder) => {
  if (reminder?.response_status === "confirmed" || reminder?.confirmed_at) {
    return {
      label: "Confermato",
      tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
      note: "Il record risulta confermato.",
    };
  }
  if (reminder?.response_status === "declined" || reminder?.response_status === "cancelled") {
    return {
      label: "Declinato",
      tone: "border-rose-200 bg-rose-50 text-rose-700",
      note: `Stato tecnico: ${reminder.response_status}`,
    };
  }
  if (reminder?.status === "sent") {
    return {
      label: "Preparato",
      tone: "border-amber-200 bg-amber-50 text-amber-700",
      note: "Promemoria preparato. Nessun invio reale viene implicato in questa pagina.",
    };
  }
  if (reminder?.status === "confirmed") {
    return {
      label: "Confermato",
      tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
      note: "Conferma registrata dal record tecnico.",
    };
  }
  if (reminder?.status === "skipped") {
    const missingRecipient = reminder?.status_detail === "missing_recipient";
    return {
      label: "Saltato",
      tone: "border-slate-300 bg-slate-100 text-slate-700",
      note: missingRecipient
        ? "Nessun recapito disponibile: il promemoria non è stato preparato né inviato."
        : "Il promemoria è stato saltato dalla simulazione.",
    };
  }
  return {
    label: `Stato tecnico: ${formatValue(reminder?.status, "sconosciuto")}`,
    tone: "border-slate-200 bg-slate-50 text-slate-700",
    note: "Stato mostrato in forma prudente per la simulazione owner-only.",
  };
};

const getNotificationStatusPresentation = (notification) => {
  if (notification?.confirmed_at || notification?.status === "confirmed") {
    return {
      label: "Confermato",
      tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
      note: "La proposta risulta confermata dal record tecnico.",
    };
  }
  if (isExpired(notification?.expires_at) || notification?.status === "expired") {
    return {
      label: "Scaduto",
      tone: "border-slate-300 bg-slate-100 text-slate-700",
      note: "Scadenza tecnica superata.",
    };
  }
  if (notification?.status === "sent") {
    return {
      label: "Visibile nella simulazione",
      tone: "border-sky-200 bg-sky-50 text-sky-700",
      note: "Proposta pronta nel Lab. Non significa invio reale al cliente.",
    };
  }
  return {
    label: `Stato tecnico: ${formatValue(notification?.status, "sconosciuto")}`,
    tone: "border-slate-200 bg-slate-50 text-slate-700",
    note: "Stato mostrato in forma prudente per la simulazione owner-only.",
  };
};

const getAppointmentStatusLabel = (appointment) => {
  const status = appointment?.status;
  const labels = {
    scheduled: "Programmato",
    requested: "Richiesto",
    cancelled: "Annullato",
    completed: "Completato",
    noshow: "No-show",
  };
  return labels[status] || `Stato tecnico: ${formatValue(status, "sconosciuto")}`;
};

const getRecoveryAttemptStatusLabel = (attempt) => {
  const labels = {
    notified: "Proposte preparate",
    confirmed: "Recupero confermato",
    none: "Nessun candidato utile",
    processing: "In elaborazione",
    expired: "Scaduto",
  };
  return labels[attempt?.status] || `Stato tecnico: ${formatValue(attempt?.status, "sconosciuto")}`;
};

export function CustomerInboxLabPage() {
  const [customers, setCustomers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [recoveryAttempts, setRecoveryAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");

  const openCustomerApp = (path) => {
    if (typeof window === "undefined") {
      return;
    }
    window.open(path, "_blank", "noopener,noreferrer");
  };

  const copyCustomerAppUrl = async (path) => {
    const absoluteUrl = buildCustomerAppUrl(path);
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      toast.success("URL Customer App copiato.");
    } catch (error) {
      toast.error("Impossibile copiare l'URL Customer App.");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError("");
      try {
        const [
          customersRes,
          appointmentsRes,
          remindersRes,
          notificationsRes,
          recoveryAttemptsRes,
        ] = await Promise.all([
          api.get("/customers"),
          api.get("/appointments"),
          api.get("/reminders"),
          api.get("/notifications"),
          api.get("/recovery-attempts"),
        ]);

        setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
        setAppointments(Array.isArray(appointmentsRes.data) ? appointmentsRes.data : []);
        setReminders(Array.isArray(remindersRes.data) ? remindersRes.data : []);
        setNotifications(Array.isArray(notificationsRes.data) ? notificationsRes.data : []);
        setRecoveryAttempts(Array.isArray(recoveryAttemptsRes.data) ? recoveryAttemptsRes.data : []);
      } catch (loadError) {
        setError("Impossibile caricare i dati del Customer Inbox Lab.");
        toast.error("Impossibile caricare i dati del Customer Inbox Lab.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const labCustomers = useMemo(() => {
    return customers
      .filter(isLabCustomer)
      .sort((left, right) => String(left.name || "").localeCompare(String(right.name || ""), "it"));
  }, [customers]);

  useEffect(() => {
    if (labCustomers.length === 0) {
      setSelectedCustomerId("");
      return;
    }
    if (!selectedCustomerId || !labCustomers.some((customer) => customer.id === selectedCustomerId)) {
      setSelectedCustomerId(labCustomers[0].id);
    }
  }, [labCustomers, selectedCustomerId]);

  const selectedCustomer = useMemo(() => {
    return labCustomers.find((customer) => customer.id === selectedCustomerId) || null;
  }, [labCustomers, selectedCustomerId]);

  const selectedCustomerAppointments = useMemo(() => {
    if (!selectedCustomer) {
      return [];
    }
    return appointments
      .filter((appointment) => appointment.customer_id === selectedCustomer.id)
      .sort((left, right) => Date.parse(right.start_time || 0) - Date.parse(left.start_time || 0));
  }, [appointments, selectedCustomer]);

  const appointmentIds = useMemo(() => {
    return new Set(selectedCustomerAppointments.map((appointment) => appointment.id));
  }, [selectedCustomerAppointments]);

  const selectedCustomerReminders = useMemo(() => {
    if (!selectedCustomer) {
      return [];
    }
    return reminders
      .filter((reminder) => {
        if (reminder.customer_id === selectedCustomer.id) {
          return true;
        }
        return reminder.appointment_id && appointmentIds.has(reminder.appointment_id);
      })
      .sort((left, right) => Date.parse(right.scheduled_for || 0) - Date.parse(left.scheduled_for || 0));
  }, [appointmentIds, reminders, selectedCustomer]);

  const selectedCustomerNotifications = useMemo(() => {
    if (!selectedCustomer) {
      return [];
    }
    return notifications
      .filter((notification) => notification.customer_id === selectedCustomer.id)
      .sort((left, right) => Date.parse(right.expires_at || 0) - Date.parse(left.expires_at || 0));
  }, [notifications, selectedCustomer]);

  const recoveryAttemptIds = useMemo(() => {
    return new Set(
      selectedCustomerNotifications
        .map((notification) => notification.recovery_attempt_id)
        .filter(Boolean)
    );
  }, [selectedCustomerNotifications]);

  const selectedCustomerRecoveryAttempts = useMemo(() => {
    return recoveryAttempts
      .filter((attempt) => recoveryAttemptIds.has(attempt.id))
      .sort((left, right) => Date.parse(right.expired_at || 0) - Date.parse(left.expired_at || 0));
  }, [recoveryAttemptIds, recoveryAttempts]);

  if (loading) {
    return <div data-testid="customer-inbox-lab-loading">Caricamento Customer Inbox Lab...</div>;
  }

  if (error) {
    return (
      <div
        className="bg-white border border-red-200 rounded-sm p-6 text-sm text-red-600"
        data-testid="customer-inbox-lab-error"
      >
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-inbox-lab-page">
      <div className="space-y-2">
        <h2 className="text-lg font-semibold section-title" data-testid="customer-inbox-lab-title">
          Customer Inbox Lab
        </h2>
        <div
          className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="customer-inbox-lab-banner"
        >
          {LAB_SAFETY_MESSAGE}
        </div>
        <p className="text-sm text-slate-500" data-testid="customer-inbox-lab-scope-note">
          Questa pagina simula la futura inbox cliente app-internal in modalità read-only.
        </p>
        <p className="text-sm text-slate-500" data-testid="customer-inbox-lab-owner-note">
          Questa pagina non è la customer app reale. È una vista interna owner-only per validare flussi e messaggi con dati fittizi.
        </p>
      </div>

      {labCustomers.length === 0 ? (
        <div
          className="bg-white border border-dashed border-slate-200 rounded-sm p-6 text-sm text-slate-500"
          data-testid="customer-inbox-lab-empty"
        >
          Nessun cliente Lab trovato. Crea clienti fittizi con prefisso [LAB] per usare questa pagina.
        </div>
      ) : (
        <section className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]" data-testid="customer-inbox-lab-layout">
          <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="customer-inbox-lab-customers-card">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold section-title">Clienti Lab</h3>
              <span className="text-xs uppercase tracking-[0.2em] text-slate-400">
                {labCustomers.length}
              </span>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              Mostra solo clienti con nome che inizia con `[LAB]`.
            </p>
            <div className="mt-4 space-y-3" data-testid="customer-inbox-lab-customers-list">
              {labCustomers.map((customer) => {
                const isSelected = customer.id === selectedCustomerId;
                return (
                  <button
                    key={customer.id}
                    type="button"
                    onClick={() => setSelectedCustomerId(customer.id)}
                    className={`w-full rounded-sm border p-3 text-left ${
                      isSelected
                        ? "border-slate-900 bg-slate-50"
                        : "border-slate-200 bg-white"
                    }`}
                    data-testid={`customer-inbox-lab-customer-${customer.id}`}
                  >
                    <p className="text-sm font-medium text-slate-900">{customer.name}</p>
                    <p className="mt-1 text-xs text-slate-500 break-all">
                      {customer.email || "Nessuna email"} · {customer.phone || "Nessun telefono"}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-6" data-testid="customer-inbox-lab-detail">
            {selectedCustomer && (
              <section className="bg-white border border-slate-200 rounded-sm p-6" data-testid="customer-inbox-lab-customer-detail">
                <h3 className="text-base font-semibold section-title">Cliente fittizio selezionato</h3>
                <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Nome</p>
                    <p className="mt-1 text-slate-900">{selectedCustomer.name}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Email</p>
                    <p className="mt-1 break-all text-slate-900">{selectedCustomer.email || "Nessuna email"}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Telefono</p>
                    <p className="mt-1 text-slate-900">{selectedCustomer.phone || "Nessun telefono"}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Source tecnico</p>
                    <p className="mt-1 text-slate-900">{formatValue(selectedCustomer.source, "Non impostato")}</p>
                  </div>
                </div>
              </section>
            )}

            <section className="bg-white border border-slate-200 rounded-sm p-6" data-testid="customer-inbox-lab-appointments">
              <h3 className="text-base font-semibold section-title">Appuntamenti collegati</h3>
              <p className="mt-2 text-sm text-slate-500">
                Vista read-only degli appuntamenti del cliente Lab selezionato.
              </p>
              <div className="mt-4 space-y-3">
                {selectedCustomerAppointments.length === 0 && (
                  <p className="text-sm text-slate-500" data-testid="customer-inbox-lab-appointments-empty">
                    Nessun appuntamento collegato a questo cliente Lab.
                  </p>
                )}
                {selectedCustomerAppointments.map((appointment) => (
                  <div
                    key={appointment.id}
                    className="rounded-sm border border-slate-200 p-3"
                    data-testid={`customer-inbox-lab-appointment-${appointment.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {formatDateTime(appointment.start_time)}
                        </p>
                        <p className="text-xs text-slate-500">
                          Fine: {formatDateTime(appointment.end_time)}
                        </p>
                      </div>
                      <span className="rounded-sm border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700">
                        {getAppointmentStatusLabel(appointment)}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                      <p>Servizio: {formatValue(appointment.service_name || appointment.service_id)}</p>
                      <p>Staff: {formatValue(appointment.staff_name || appointment.staff_id, "Non assegnato")}</p>
                      <p>Origine: {formatValue(appointment.origin, "Non disponibile")}</p>
                      <p>Recovery da appuntamento: {formatValue(appointment.recovered_from_appointment_id, "Nessuno")}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-sm p-6" data-testid="customer-inbox-lab-reminders">
              <h3 className="text-base font-semibold section-title">Reminder preparati</h3>
              <p className="mt-2 text-sm text-slate-500">
                Il Lab reinterpreta gli stati tecnici senza suggerire invii reali al cliente.
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Se il record possiede token pubblici già esistenti, da qui puoi aprire la Customer App di test senza passare dagli endpoint legacy diretti.
              </p>
              <div className="mt-4 space-y-3">
                {selectedCustomerReminders.length === 0 && (
                  <p className="text-sm text-slate-500" data-testid="customer-inbox-lab-reminders-empty">
                    Nessun reminder collegato a questo cliente Lab.
                  </p>
                )}
                {selectedCustomerReminders.map((reminder) => {
                  const statusPresentation = getReminderStatusPresentation(reminder);
                  return (
                    <div
                      key={reminder.id}
                      className="rounded-sm border border-slate-200 p-3"
                      data-testid={`customer-inbox-lab-reminder-${reminder.id}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {formatValue(reminder.reminder_type, "Reminder")}
                          </p>
                          <p className="text-xs text-slate-500">
                            Pianificato: {formatDateTime(reminder.scheduled_for)}
                          </p>
                        </div>
                        <span className={`rounded-sm border px-2 py-1 text-xs ${statusPresentation.tone}`}>
                          {statusPresentation.label}
                        </span>
                      </div>
                      <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                        <p>Canale tecnico: {formatValue(reminder.channel, "Non disponibile")}</p>
                        <p>Appuntamento: {formatValue(reminder.appointment_id, "Non disponibile")}</p>
                        <p>Risposta tecnica: {formatValue(reminder.response_status, "Non disponibile")}</p>
                        <p>Confermato il: {formatDateTime(reminder.confirmed_at)}</p>
                      </div>
                      {(reminder.confirm_token || reminder.cancel_token) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {reminder.confirm_token && (
                            <>
                              <button
                                type="button"
                                onClick={() => openCustomerApp(`/customer/reminders/confirm/${encodeURIComponent(reminder.confirm_token)}`)}
                                className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700"
                                data-testid={`customer-inbox-lab-reminder-confirm-open-${reminder.id}`}
                              >
                                Apri Customer App conferma
                              </button>
                              <button
                                type="button"
                                onClick={() => copyCustomerAppUrl(`/customer/reminders/confirm/${encodeURIComponent(reminder.confirm_token)}`)}
                                className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700"
                                data-testid={`customer-inbox-lab-reminder-confirm-copy-${reminder.id}`}
                              >
                                Copia URL conferma
                              </button>
                            </>
                          )}
                          {reminder.cancel_token && (
                            <>
                              <button
                                type="button"
                                onClick={() => openCustomerApp(`/customer/reminders/cancel/${encodeURIComponent(reminder.cancel_token)}`)}
                                className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700"
                                data-testid={`customer-inbox-lab-reminder-cancel-open-${reminder.id}`}
                              >
                                Apri Customer App cancellazione
                              </button>
                              <button
                                type="button"
                                onClick={() => copyCustomerAppUrl(`/customer/reminders/cancel/${encodeURIComponent(reminder.cancel_token)}`)}
                                className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700"
                                data-testid={`customer-inbox-lab-reminder-cancel-copy-${reminder.id}`}
                              >
                                Copia URL cancellazione
                              </button>
                            </>
                          )}
                        </div>
                      )}
                      <p className="mt-3 text-xs text-slate-500">{statusPresentation.note}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-sm p-6" data-testid="customer-inbox-lab-notifications">
              <h3 className="text-base font-semibold section-title">Notification / Recovery preparate</h3>
              <p className="mt-2 text-sm text-slate-500">
                Questa vista owner-only non mostra token in chiaro, ma può aprire la Customer App di test quando il record tecnico dispone già di un token recovery.
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Per i record tecnici che hanno già un token recovery, puoi aprire o copiare la Customer App di test come adapter temporaneo.
              </p>
              <div className="mt-4 space-y-3">
                {selectedCustomerNotifications.length === 0 && (
                  <p className="text-sm text-slate-500" data-testid="customer-inbox-lab-notifications-empty">
                    Nessuna notification o proposta recovery collegata a questo cliente Lab.
                  </p>
                )}
                {selectedCustomerNotifications.map((notification) => {
                  const statusPresentation = getNotificationStatusPresentation(notification);
                  return (
                    <div
                      key={notification.id}
                      className="rounded-sm border border-slate-200 p-3"
                      data-testid={`customer-inbox-lab-notification-${notification.id}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            Recovery attempt {formatValue(notification.recovery_attempt_id, "Non disponibile")}
                          </p>
                          <p className="text-xs text-slate-500">
                            Scadenza tecnica: {formatDateTime(notification.expires_at)}
                          </p>
                        </div>
                        <span className={`rounded-sm border px-2 py-1 text-xs ${statusPresentation.tone}`}>
                          {statusPresentation.label}
                        </span>
                      </div>
                      <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                        <p>Canale tecnico: {formatValue(notification.channel, "Non disponibile")}</p>
                        <p>Candidato appointment: {formatValue(notification.candidate_appointment_id, "Non disponibile")}</p>
                        <p>Slot appointment: {formatValue(notification.slot_appointment_id, "Non disponibile")}</p>
                        <p>Confermato il: {formatDateTime(notification.confirmed_at)}</p>
                      </div>
                      {notification.token && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => openCustomerApp(`/customer/recovery/confirm/${encodeURIComponent(notification.token)}`)}
                            className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700"
                            data-testid={`customer-inbox-lab-notification-open-${notification.id}`}
                          >
                            Apri Customer App recovery
                          </button>
                          <button
                            type="button"
                            onClick={() => copyCustomerAppUrl(`/customer/recovery/confirm/${encodeURIComponent(notification.token)}`)}
                            className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700"
                            data-testid={`customer-inbox-lab-notification-copy-${notification.id}`}
                          >
                            Copia URL recovery
                          </button>
                        </div>
                      )}
                      <p className="mt-3 text-xs text-slate-500">{statusPresentation.note}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-sm p-6" data-testid="customer-inbox-lab-recovery-attempts">
              <h3 className="text-base font-semibold section-title">Recovery attempts collegati</h3>
              <p className="mt-2 text-sm text-slate-500">
                Collegamento ricostruito in modo semplice a partire dalle notification del cliente Lab.
              </p>
              <div className="mt-4 space-y-3">
                {selectedCustomerRecoveryAttempts.length === 0 && (
                  <p className="text-sm text-slate-500" data-testid="customer-inbox-lab-recovery-attempts-empty">
                    Nessun recovery attempt ricostruibile in modo semplice per questo cliente Lab.
                  </p>
                )}
                {selectedCustomerRecoveryAttempts.map((attempt) => (
                  <div
                    key={attempt.id}
                    className="rounded-sm border border-slate-200 p-3"
                    data-testid={`customer-inbox-lab-recovery-attempt-${attempt.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          Tentativo {attempt.id}
                        </p>
                        <p className="text-xs text-slate-500">
                          Stato: {getRecoveryAttemptStatusLabel(attempt)}
                        </p>
                      </div>
                      <span className="rounded-sm border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700">
                        Scade: {formatDateTime(attempt.expired_at)}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                      <p>Cancellazione origine: {formatValue(attempt.cancellation_id, "Non disponibile")}</p>
                      <p>Slot appointment: {formatValue(attempt.slot_appointment_id, "Non disponibile")}</p>
                      <p>Appuntamento selezionato: {formatValue(attempt.selected_appointment_id, "Non selezionato")}</p>
                      <p>Candidati: {Array.isArray(attempt.candidate_appointment_ids) ? attempt.candidate_appointment_ids.length : 0}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </section>
      )}
    </div>
  );
}
